// useReviewShortcuts — J/K move between proposal cards, A approve, D deny, E
// edit, ? lists the shortcuts (standing-agent-ui "Keyboard review"; design D10).
// Inactive while typing: any event whose target is an input, textarea, select,
// contenteditable, or inside a Kendo editor/input/dropdown is ignored, as are
// chords with Ctrl/Meta/Alt so browser shortcuts keep working.

import { useEffect, useRef } from 'react';

export interface ReviewShortcutHandlers {
  next: () => void;
  previous: () => void;
  approve: () => void;
  deny: () => void;
  edit: () => void;
  help: () => void;
}

const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], .k-input, .k-textarea, .k-editor, .k-dropdownlist, .k-combobox, .k-numerictextbox, .k-dialog';

/** True when a key event should be left to the focused control. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  return target.closest(EDITABLE_SELECTOR) != null;
}

export function useReviewShortcuts(handlers: ReviewShortcutHandlers, enabled = true) {
  const ref = useRef(handlers);
  useEffect(() => {
    ref.current = handlers;
  }, [handlers]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      const h = ref.current;
      switch (e.key) {
        case 'j':
        case 'J':
          h.next();
          break;
        case 'k':
        case 'K':
          h.previous();
          break;
        case 'a':
        case 'A':
          h.approve();
          break;
        case 'd':
        case 'D':
          h.deny();
          break;
        case 'e':
        case 'E':
          h.edit();
          break;
        case '?':
          h.help();
          break;
        default:
          return;
      }
      e.preventDefault();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled]);
}
